import { type ForecastAccuracyData } from '@/hooks/admin/useAdminForecastAccuracy';
import { AdminTabShell } from './AdminTabShell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ForecastAccuracyTabProps {
  data: ForecastAccuracyData | null;
  isLoading: boolean;
  error: Error | null;
  lookbackDays: number;
  onLookbackChange: (days: number) => void;
  orgId: string | null;
  onOrgChange: (orgId: string | null) => void;
}

function MetricCard({ label, value, unit }: { label: string; value: number | null; unit: string }) {
  return (
    <div className="bg-wt-bg-secondary border border-wt-border rounded-lg p-3">
      <div className="text-xs text-wt-text-muted uppercase">{label}</div>
      <div className="text-lg font-mono text-wt-text-primary mt-1">
        {value != null ? value.toFixed(2) : '--'}
        <span className="text-xs text-wt-text-muted ml-1">{unit}</span>
      </div>
    </div>
  );
}

function ConfidenceRow({ label, count, avgError }: { label: string; count: number; avgError: number | null }) {
  return (
    <tr className="hover:bg-wt-bg-secondary/50">
      <td className="px-3 py-1.5 text-wt-text-secondary capitalize">{label}</td>
      <td className="px-3 py-1.5 text-right font-mono text-wt-text-secondary">{count}</td>
      <td className="px-3 py-1.5 text-right font-mono text-wt-text-secondary">
        {avgError != null ? avgError.toFixed(2) : '--'}
      </td>
    </tr>
  );
}

export function ForecastAccuracyTab({
  data, isLoading, error, lookbackDays, onLookbackChange, orgId, onOrgChange,
}: ForecastAccuracyTabProps) {
  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {data && (
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex items-center gap-3">
            <Select value={String(lookbackDays)} onValueChange={(v) => onLookbackChange(Number(v))}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>

            <Select value={orgId ?? 'all'} onValueChange={(v) => onOrgChange(v === 'all' ? null : v)}>
              <SelectTrigger className="w-[200px] h-8 text-xs">
                <SelectValue placeholder="All Orgs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Orgs</SelectItem>
                {data.orgs.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name} ({o.property_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fleet aggregate */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Total Forecasts" value={data.fleetAggregate.total_forecasts} unit="" />
            <MetricCard label="MAE" value={data.fleetAggregate.avg_error_kwh} unit="kWh" />
            <MetricCard label="MAPE" value={data.fleetAggregate.avg_error_pct} unit="%" />
            <MetricCard label="Median Error" value={data.fleetAggregate.median_error_kwh} unit="kWh" />
          </div>

          {/* Confidence breakdown */}
          <div className="border border-wt-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-wt-bg-tertiary text-wt-text-muted text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Confidence</th>
                  <th className="px-3 py-2 text-right">Count</th>
                  <th className="px-3 py-2 text-right">Avg Error (kWh)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-wt-border">
                <ConfidenceRow label="high" count={data.fleetAggregate.by_confidence.high.count} avgError={data.fleetAggregate.by_confidence.high.avg_error_kwh} />
                <ConfidenceRow label="medium" count={data.fleetAggregate.by_confidence.medium.count} avgError={data.fleetAggregate.by_confidence.medium.avg_error_kwh} />
                <ConfidenceRow label="low" count={data.fleetAggregate.by_confidence.low.count} avgError={data.fleetAggregate.by_confidence.low.avg_error_kwh} />
              </tbody>
            </table>
          </div>

          {/* Per-property table */}
          {data.perProperty.length > 0 && (
            <div className="border border-wt-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-wt-bg-tertiary text-wt-text-muted text-xs uppercase tracking-wider">
                    <th className="px-3 py-2 text-left">Property</th>
                    <th className="px-3 py-2 text-left">Org</th>
                    <th className="px-3 py-2 text-right">Forecasts</th>
                    <th className="px-3 py-2 text-right">Avg Error (kWh)</th>
                    <th className="px-3 py-2 text-right">Avg Error (%)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-wt-border">
                  {data.perProperty.map((p) => (
                    <tr key={p.property_id} className="hover:bg-wt-bg-secondary/50">
                      <td className="px-3 py-2">
                        <a
                          href={`/app/property/${p.property_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-wt-text-primary hover:underline"
                        >
                          {p.property_name}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-wt-text-secondary">{p.org_name}</td>
                      <td className="px-3 py-2 text-right font-mono text-wt-text-secondary">{p.forecast_count}</td>
                      <td className="px-3 py-2 text-right font-mono text-wt-text-secondary">
                        {p.avg_error_kwh != null ? Number(p.avg_error_kwh).toFixed(2) : '--'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-wt-text-secondary">
                        {p.avg_error_pct != null ? Number(p.avg_error_pct).toFixed(1) : '--'}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AdminTabShell>
  );
}
